/**
 * this component will display the card summary information
 *
 * @module views/components/SummaryCard
 * @memberof -Common
 */
import './SummaryCard.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Text as PalText } from 'web-palette/dist/components/Text/Text';

import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';

import { ClickableBadge } from '@ulta/components/ClickableBadge/ClickableBadge';



/**
 * Represents a SummaryCard component
 *
 * @method
 * @param {SummaryCardProps} props - React properties passed from composition
 * @returns SummaryCard
 */
export const SummaryCard = function( props ){
  const { type, icon, title, subTitle, items, layout, onItemSelection } = props;

  return (
    <div className='SummaryCard'>
      <div>
        { icon && (
          <Icon
            size='l'
            name={ icon }
            className='SummaryCard--titleIcon'
            aria-hidden={ true }
          />
        ) }
      </div>
      <div>
        { title && (
          <PalText htmlTag='h2'
            textStyle='title-sm'
          >
            { title }
          </PalText>
        ) }
        { subTitle && (
          <Text textStyle='body-sm'
            color='neutral-500'
          >
            { subTitle }
          </Text>
        ) }
        <ul className={ classNames( 'SummaryCard--listWrapper', {
          ...( layout && { [`SummaryCard--${layout}`]: layout } )
        } )
        }
        >
          { items?.map( ( item, index )=>(
            <SummaryCardItem
              key={ `summary-card-item-${index}-${item?.label}` }
              item={ item }
              index={ index }
              type={ type }
              onItemSelection={ onItemSelection }
            />
          ) ) }
        </ul>
      </div>
    </div>
  );
};

export const summaryCardVarinats = {
  HALF : 'Half',
  FULL : 'Full'
};

/**
 * Return a li element containing the summary card item.
 *
 * @param {Object} props - The props passed to the component.
 * @param {Object} props.item - item object for summary card item.
 * @param {string} props.index - index of summary card item.
 * @param {string} props.type - type of summary card item.
 * @param {Function} props.onItemSelection - Function when summary card item is selected.
 * @returns {JSX.Element} returns the `li` component.
 */
export const SummaryCardItem = ( props ) => {
  const { item, index, type, onItemSelection } = props || {};
  if( !item ){
    return null;
  }
  const openerRef = useRef();
  return (
    <li
      className={ classNames( 'SummaryCard--listItem', {
        'SummaryCard--listItem--halfWidth': type === summaryCardVarinats.HALF
      } ) }
    >
      <ClickableBadge item={ item }
        openerRef={ openerRef }
        onClick={ () => onItemSelection( item.itemDetailCard, openerRef ) }
      />
    </li>
  );
};

/**
 * Property type definitions
 * @typedef SummaryCardProps
 * @type {object}
 * @property {string} type - Sets value for the type
 * @property {string} icon - Sets value for an icon
 * @property {string} title - Sets value for the title
 * @property {object} items - Array of items required for summary card
 * @property {string} layout - Sets value for the layout
 */
export const propTypes =  {
  type: PropTypes.oneOf( ['Full', 'Half'] ),
  icon: PropTypes.string,
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      icon: PropTypes.string,
      label: PropTypes.string
    } )
  ),
  layout: PropTypes.string.isRequired
};

SummaryCard.propTypes = propTypes;

export default SummaryCard;